/** @format */
'use client';
import { cssVars } from '@atoms/GlobalStyles';
import useTimeout from '@common/application/hooks/useTimeout/src';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { keyframes } from 'styled-components';
import styles from './Notification.module.css';

enum NOTIFICATION {
  INFO,
  ALERT,
  ERROR,
  SUCCESS,
}

interface AddNotification {
  message: string | React.ReactNode;
  variant?: NOTIFICATION;
  indefinite?: boolean;
}

interface Notification {
  indefinite?: boolean;
  id: number;
  message: string | React.ReactNode;
  variant: NOTIFICATION;
}

function getBackgroundColor(variant: NOTIFICATION) {
  switch (variant) {
    case NOTIFICATION.INFO:
      return 'var(--primary-500)';
    case NOTIFICATION.ALERT:
      return cssVars.yellow;
    case NOTIFICATION.ERROR:
      return cssVars.red;
    case NOTIFICATION.SUCCESS:
      return cssVars.green;
    default:
      return cssVars.green;
  }
}

function getColor(variant: NOTIFICATION) {
  switch (variant) {
    case NOTIFICATION.INFO:
      return 'white';
    case NOTIFICATION.ALERT:
      return 'black';
    case NOTIFICATION.ERROR:
      return 'white';
    case NOTIFICATION.SUCCESS:
      return 'black';
    default:
      return 'black';
  }
}

// noinspection JSUnusedGlobalSymbols
const defaultApi = {
  notifications: [] as Notification[],
  notify: (notification: AddNotification) => {
    /*default*/
  },
  clearNotification: (id: number) => {
    /*default*/
  },
};

type NotificationsContextValue = typeof defaultApi;

/**
 * Create Context
 */
const NotificationsContext = React.createContext<NotificationsContextValue>(defaultApi);

function Notification({ variant, id, message, clearNotification, indefinite }: Notification & { clearNotification: (id: number) => void }) {
  const timeoutTime = 3000;
  useTimeout(() => clearNotification(id), !indefinite ? timeoutTime : null);

  return (
    <div key={id} style={{ backgroundColor: getBackgroundColor(variant), color: getColor(variant) }} onClick={() => clearNotification(id)} className={styles.notification}>
      <span className={styles.header}>
        <FontAwesomeIcon className={styles.closeIcon} icon={['fas', 'multiply']} />
      </span>
      <div>
        {message}
        {!indefinite && (
          <div className={styles.progressWrapper}>
            <div className={styles.progressBar} style={{ animationDuration: `${timeoutTime / 1000}s` }} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Custom Notifications Provider
 */
function NotificationsProvider({ children }: any) {
  // Notifications queue is managed in local useState
  const [notifications, setNotifications] = React.useState<Notification[]>(defaultApi.notifications);

  // Method to push a new notification
  const notify = React.useCallback(
    (notification: AddNotification) => {
      if (notifications.map((n) => n.message).includes(notification.message)) return;
      setNotifications((notifications) => [
        ...notifications,
        {
          id: new Date().getTime(), // we use UIDs in the final ver
          variant: NOTIFICATION.ERROR,
          ...notification,
        } as Notification,
      ]);
    },
    [setNotifications],
  );

  // Method to clear a notification
  const clearNotification = React.useCallback(
    (id: number) => {
      const nextNotifications = notifications.filter((n) => n.id !== id);
      setNotifications(nextNotifications);
    },
    [notifications, setNotifications],
  );

  // Return Provider with full API
  const api = { notifications, notify, clearNotification };
  return (
    <NotificationsContext.Provider value={api}>
      {children}
      <div className={styles.notificationContainer}>
        {/*Slice notifications to limit number shown at any time*/}
        {notifications.slice(0, 2).map((notification) => (
          <Notification key={notification.id} {...notification} clearNotification={clearNotification} />
        ))}
      </div>
    </NotificationsContext.Provider>
  );
}

// Convenience import hook
function useNotifications() {
  return React.useContext(NotificationsContext);
}

export { useNotifications, NOTIFICATION, NotificationsProvider, NotificationsContext };
